import React, { useEffect, useState } from "react";
import Loader from "../../components/loader";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  BIG_FIVE_TEST_ASSESSMENT_ID,
  CONTENT_KEY_BIG_FIVE,
  MODEL_TYPE_ADHD,
  PERSONALITY_TEST_ASSESSMENT_ID,
} from "../../utils/general-config";

import { getAdhdScoreCategory } from "../../utils/assessment-utils";

import Footer from "../../components/footer";
import BackToTopButton from "../../components/back-to-top/component";
import Header from "../../components/header";

import {
  getADHDImage,
  getEnneagramBannerImage,
} from "../../utils/utility-functions";
import ShareButton from "../../components/share-button";
import SendToFriendButton from "../../components/send-to-friend-button";
import ExpandableSection from "../../components/expandable-collapsible/component";

import { useTranslation } from "react-i18next";
import {
  APP_NAVIGATION,
  ASSESSMENT_BACKGROUND_COLORS,
  LOCALE,
} from "../../utils/constants";

import { CallToActionSection } from "../../components/call-to-action/component";
import InviteFriend from "../../components/invite-friend";
import PathHelper from "../../utils/path-helper";
import ReportWrapper from "../../components/report-wrapper";

const ADHDReport = ({
  getAssessmentResultByAssessmentAttemptIdConnect,
  getInsightfulContentConnect,
  getUserByIdConnect,
  sendToFriendReportOnEmailConnect,
  funnelStep,
  getAssessmentConnect,
  getAssessmentAttemptByUserIdConnect,

  getGuestUserConnect,
  getOrderDataByUserIdConnect,
  getBillingConnect,
  guestUserChangesConnect,
  isGuestOrPremiumUserConnect,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [currentAssessmentAttemptData, setCurrentAssessmentAttemptData] =
    useState("");
  const [contentData, setContentData] = useState({});
  const [userTraits, setUserTraits] = useState([]);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const assessmentAttemptId = queryParams.get("aai");
  const navigate = useNavigate();

  const { userGUID, isAuth, selectedPlan } = funnelStep || {};
  const [userData, setUserData] = useState(null);
  // const token = getCookie("jwtToken");
  const assessmentId = currentAssessmentAttemptData?.assessmentId;
  const userId = queryParams.get("uid") || userGUID;
  const { locale } = useParams();
  const currentLocale = locale ?? LOCALE.EN;
  const { t } = useTranslation();
  const quizPath = PathHelper(APP_NAVIGATION.QUIZ, currentLocale);

  const pathSegments = location.pathname.split("/");
  const reportType = pathSegments[4];

  useEffect(() => {
    if (!assessmentAttemptId) return;

    const fetchAssessmentAndContent = async () => {
      setIsLoading(true); // Start loading
      try {
        // Fetch user traits
        const traitsResponse =
          await getAssessmentResultByAssessmentAttemptIdConnect(
            assessmentAttemptId,
            currentLocale
          );
        if (traitsResponse) {
          setUserTraits(traitsResponse.userTraits);
          setCurrentAssessmentAttemptData(traitsResponse);
        }

        // Fetch content
        const contentResponse = await getInsightfulContentConnect({
          modelType:
            MODEL_TYPE_ADHD[currentLocale] || MODEL_TYPE_ADHD[LOCALE.EN],
          contentKey: CONTENT_KEY_BIG_FIVE,
        });
        if (contentResponse) {
          const { content } = contentResponse;

          setContentData(content);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false); // End loading
      }
    };

    fetchAssessmentAndContent();
  }, [assessmentAttemptId, currentLocale]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [isLoading]);

  useEffect(() => {
    if (userId && isAuth) {
      getUserByIdConnect(userId).then((res) => {
        if (res) {
          setUserData(res);
        }
      });
    }
  }, [userId, isAuth]);

  const bannerImage = getEnneagramBannerImage();
  const adhdResult1Image = "adhd-result-1";
  const adhdResult2Image = "adhd-result-2";

  const bannerImage2 = getADHDImage(adhdResult2Image);
  const bannerImage1 = getADHDImage(adhdResult1Image);

  const colorVariants = {
    purple: "from-purple-600 via-purple-500",
    teal: "from-teal-600 via-teal-500",
    blue: "from-blue-600 via-blue-500",
    red: "from-red-600 via-red-500",
    green: "from-green-600 via-green-500",
    yellow: "from-yellow-600 via-yellow-500",
    indigo: "from-indigo-600 via-indigo-500",
    pink: "from-pink-600 via-pink-500",
  };

  const progressBarColor = [
    "#ff6284",
    "#49A2E6",
    "#805bec",
    "#ffce55",
    "#4cc0c0",
    "#ec6af9",
  ];

  const colors = [
    "#eff4fd",
    "#f8f4fd",
    "#f2fcf9",
    "#fcf6ec",
    "#feeded",
    "#c7edf9",
    "#e7feeb",
    "#eff4fd",
    "#eae2fd",
  ];

  const descriptiveCardColorMap = [
    { bg: "bg-blue-50", text: "text-blue-800" },
    { bg: "bg-purple-50", text: "text-purple-800" },
    { bg: "bg-amber-50", text: "text-amber-800" },
    { bg: "bg-green-50", text: "text-green-800" },
    { bg: "bg-pink-50", text: "text-pink-800" },
    { bg: "bg-sky-50", text: "text-sky-800" },
    { bg: "bg-orange-50", text: "text-orange-800" },
    { bg: "bg-emerald-50", text: "text-emerald-800" },
    { bg: "bg-violet-50", text: "text-violet-800" },
  ];

  const formatSourceContent = (content) => {
    const words = content?.split(" ");

    return (
      <p className="text-gray-700">
        {words.map((word, index) => {
          const isLink =
            word.startsWith("http://") || word.startsWith("https://");

          return isLink ? (
            <a
              key={index}
              href={word}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#805fe6]"
            >
              {word}
            </a>
          ) : (
            <span key={index}> {word} </span>
          );
        })}
      </p>
    );
  };

  const TakeTestButton = ({ onClick, t, id }) => {
    return (
      <button
        id={id}
        className="flex items-center gap-2 px-6 py-3 bg-white text-black font-semibold rounded-full shadow-md border border-gray-300 hover:bg-gray-100 transition-all"
        onClick={onClick}
      >
        {t("report.adhd.take_test_cta")}
        <svg
          fill="black"
          width="20"
          height="20"
          viewBox="-2 -2 24 24"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="xMinYMin"
          className="jam jam-arrow-circle-right"
        >
          <path d="M10 20C4.477 20 0 15.523 0 10S4.477 0 10 0s10 4.477 10 10-4.477 10-10 10zm0-2a8 8 0 1 0 0-16 8 8 0 0 0 0 16zm1.586-7H6a1 1 0 0 1 0-2h5.586L9.05 6.464a1 1 0 1 1 1.414-1.414l4.243 4.243a.997.997 0 0 1 0 1.414l-4.243 4.243a1 1 0 1 1-1.414-1.414L11.586 11z" />
        </svg>
      </button>
    );
  };

  const HeroBanner = ({ title, color = "purple", buttonNeeded, onClick }) => {
    const gradientClasses = colorVariants[color] || colorVariants.purple;

    return (
      <div
        className={`relative h-auto bg-gradient-to-r ${gradientClasses} to-purple-600`}
      >
        {/* Background Image (Hidden on small screens) */}
        <img
          src={bannerImage}
          alt={title}
          className="absolute bottom-0 right-0 w-full md:w-2/3 h-full object-cover object-right"
        />

        {/* Gradient Overlay */}
        <div
          className={`block absolute inset-0 bg-gradient-to-r ${gradientClasses} to-transparent`}
        />
        <div className="absolute inset-0 bg-black opacity-10" />

        {/* Hero Content */}
        <div className="relative h-full flex items-center" id={reportType}>
          <div className="w-full px-4 py-8 lg:px-8 lg:py-12" id={reportType}>
            {/* Title & Button Wrapper */}
            <div className="flex flex-col md:flex-row items-start justify-between gap-4 pr-10 md:pr-16">
              {/* Title */}
              <h2
                id={reportType}
                style={{ textShadow: "2px 2px 4px rgba(0, 0, 0, 0.5)" }}
                className="text-3xl md:text-4xl font-bold text-white break-words whitespace-pre-wrap text-left"
              >
                {title}
              </h2>

              {/* Button (if needed) */}
              {buttonNeeded && (
                <div className="flex-shrink-0">
                  <TakeTestButton onClick={onClick} t={t} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const InfoListCard = ({ heading, bgClr, contentArray, overview }) => {
    return (
      <div
        style={{ backgroundColor: `${bgClr}` }}
        className="p-4 md:p-6 rounded-2xl shadow-lg flex flex-col h-full w-full break-words overflow-hidden"
      >
        {/* Heading */}
        {heading && (
          <h2 className="text-lg font-semibold text-gray-700 break-words">
            {heading}
          </h2>
        )}

        {/* Overview (Shouldn't cause extra white space) */}
        {overview && (
          <p className="text-lg text-gray-700 mt-2 break-words max-w-full overflow-hidden">
            {overview}
          </p>
        )}

        {/* Content List */}
        <ul className="list-none space-y-3 mt-4">
          {contentArray &&
            contentArray.map((item, index) => (
              <li key={index} className="flex gap-4 w-full">
                <div className="w-8 h-8 flex-shrink-0">
                  <svg
                    width="100%"
                    height="100%"
                    viewBox="0 0 48 48"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <defs>
                      <filter
                        id="shadow"
                        x="-20%"
                        y="-20%"
                        width="140%"
                        height="140%"
                      >
                        <feDropShadow
                          dx="0"
                          dy="2"
                          stdDeviation="3"
                          floodColor="black"
                          floodOpacity="0.4"
                        />
                      </filter>
                    </defs>
                    <path
                      d="M24 33C28.9706 33 33 28.9706 33 24C33 19.0294 28.9706 15 24 15C19.0294 15 15 19.0294 15 24C15 28.9706 19.0294 33 24 33Z"
                      fill="#805fe6"
                      stroke="#fff"
                      strokeWidth="6"
                      filter="url(#shadow)"
                    />
                  </svg>
                </div>
                <p className="flex-1 break-words overflow-hidden">
                  <strong>{item?.Heading}</strong>:{" "}
                  {Array.isArray(item?.Content) ? (
                    item.Content.map((subItem, subIndex) => (
                      <div key={subIndex} className="break-words">
                        <strong>{subItem.Heading}</strong>:{" "}
                        {formatSourceContent(subItem.Content)}
                      </div>
                    ))
                  ) : (
                    <div className="break-words">
                      {formatSourceContent(item?.Content)}
                    </div>
                  )}
                </p>
              </li>
            ))}
        </ul>
      </div>
    );
  };

  const CircleIcon = ({ character = "1", size = 24, color = "#7D5FFF" }) => {
    const viewBoxSize = 100;
    const centerPoint = viewBoxSize / 2;

    const minSize = 16;
    const actualSize = Math.max(size, minSize);
    const isBullet = character === "";
    const isQuestion = character === "?";
    const isNumber = !isNaN(parseInt(character));
    let finalColor;

    if (color !== "#7D5FFF") {
      finalColor = "#336bcc";
    } else {
      if (isQuestion) {
        finalColor = "#805bec";
      } else if (isNumber) {
        finalColor = "#805bec";
      } else {
        finalColor = "#805bec";
      }
    }

    const textSize = isQuestion ? 60 : 50;

    return (
      <svg
        width={actualSize}
        height={actualSize}
        viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
        className="min-w-[16px] min-h-[16px]"
      >
        <circle
          cx={centerPoint}
          cy={centerPoint}
          r={isBullet ? 42 : 48}
          fill={finalColor}
          filter={`url(#shadow-${character || "bullet"}-${actualSize})`}
        />

        <text
          x={centerPoint}
          y={centerPoint + (isQuestion ? 4 : 2)}
          fontSize={textSize}
          fontWeight="bold"
          fill="white"
          textAnchor="middle"
          dominantBaseline="middle"
          fontFamily="Arial, sans-serif"
        >
          {character}
        </text>
      </svg>
    );
  };

  const DescriptiveCard = ({
    heading,
    bgClr,
    contentArray,
    overview,
    character,
    isSubHeading = false,
    textClr,
  }) => {
    return (
      <div
        className={`p-4 md:p-6 rounded-2xl shadow-lg ${bgClr} flex flex-col h-full`}
      >
        {heading && (
          <div className="flex items-start gap-2 mb-2">
            <div>
              <div className="flex items-start gap-2">
                {character && (
                  <div className="shrink-0 flex items-center justify-center rounded-full border-4 border-white shadow-[0_0_5px_rgba(0,0,0,1)]">
                    {" "}
                    <CircleIcon character={character} size={20} />
                  </div>
                )}

                <h2
                  className={`text-lg sm:text-xl ${textClr}  font-semibold  flex items-center`}
                >
                  {heading}
                </h2>
              </div>
            </div>
          </div>
        )}

        {overview && (
          <p
            className={`text-lg text-gray-700 mt-4 ${
              character ? "pl-8 md:pl-10" : ""
            } ${contentArray ? `mb-4` : ""}`}
          >
            {overview}
          </p>
        )}
        {contentArray && (
          <ul
            className={`space-y-2 mt-4 ${
              overview ? "pl-8 md:pl-12" : "md:pl-6 pl-2"
            }`}
          >
            {contentArray?.map((item, index) => {
              return (
                <li key={index} className="flex gap-4">
                  {/* Left Section: Circle Icon */}
                  <div className="w-4 h-4 flex items-center justify-center rounded-full border-2 border-white shadow-[0_0_5px_rgba(0,0,0,1)] mt-[5px]">
                    <CircleIcon character={""} size={16} />
                  </div>

                  {/* Right Section: Heading & Content */}
                  <div className="flex flex-col">
                    {isSubHeading ? (
                      <>
                        {/* Heading and First Line in Same Row */}
                        <div className="flex items-center gap-2">
                          <h2 className="text-lg font-semibold text-gray-700">
                            {item?.heading}
                          </h2>
                        </div>

                        {/* Paragraph with text-indent effect */}
                        <p className="text-gray-700 text-base md:text-lg leading-relaxed relative">
                          <span className="">{item.content}</span>
                        </p>
                      </>
                    ) : (
                      <p className="text-gray-700 text-base md:text-lg leading-relaxed">
                        {item}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  };

  const CallToActionSectionForUnAuth = ({ onClick1, onClick2 }) => {
    return (
      <section className="space-y-6" id="cta-section">
        <div
          className="block bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
          id="cta-personality-card"
        >
          <div
            className="bg-gradient-to-r from-blue-600 to-indigo-600 p-8 justify-center items-center flex flex-col gap-2"
            id="cta-personality-content"
          >
            <h3
              className="text-xl font-bold text-white text-center"
              id="cta-personality-title"
            >
              {t("report.adhd.testMessage")}
            </h3>

            <p
              className="text-white opacity-90 text-center"
              id="cta-personality-description"
            >
              {t("report.adhd.discoverMessage")}
            </p>

            <TakeTestButton
              id="cta-take-personality-test-btn"
              onClick={onClick1}
              t={t}
            />
          </div>
        </div>

        <div
          className="block bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
          id="cta-bigfive-card"
        >
          <div
            className="bg-gradient-to-r from-purple-600 to-pink-600 p-8 flex flex-col justify-center items-center gap-2"
            id="cta-bigfive-content"
          >
            <h3
              className="text-xl font-bold text-white text-center"
              id="cta-bigfive-title"
            >
              {t("report.adhd.bigFiveMessage")}
            </h3>

            <p
              className="text-white opacity-90 text-center"
              id="cta-bigfive-description"
            >
              {t("report.adhd.strengthMesssge")}
            </p>

            <TakeTestButton
              id="cta-take-bigfive-test-btn"
              onClick={onClick2}
              t={t}
            />
          </div>
        </div>
      </section>
    );
  };

  function transformUserTraitsData(data) {
    if (!Array.isArray(data)) return { result: {}, totalScore: 0 };

    const result = data.reduce((acc, item) => {
      if (
        item?.trait?.title &&
        typeof item.percentage === "number" &&
        typeof item.score === "number"
      ) {
        acc[item.trait.title] = {
          percentage: item.percentage,
          score: item.score,
        };
      }
      return acc;
    }, {});

    const totalScore = data.reduce((sum, item) => sum + (item?.score || 0), 0);

    return { result, totalScore };
  }

  function transformAssessmentData(data) {
    if (!Array.isArray(data)) return {};

    const formattedData = {};

    data.forEach((section, index) => {
      if (!section?.EnglishCode) return;
      const key = section.EnglishCode.split(":")[0]
        .replace(/\s+/g, "")
        .toLowerCase();
      formattedData[key] = {
        Heading: section.Heading,
        ...(section.FlavorText && { FlavorText: section.FlavorText }),
        Content: {},
      };

      if (Array.isArray(section.Content)) {
        section.Content.forEach((item) => {
          if (!item?.Heading || !item?.Content) return;
          const itemKey = item?.EnglishCode?.toLowerCase();
          formattedData[key].Content[itemKey] = {
            Content: item.Content,
            Heading: item.Heading,
          };
        });
      }
      if (section.ContentFollowUp) {
        formattedData[key].ContentFollowUp = section.ContentFollowUp;
      }
    });
    return formattedData;
  }

  function getOverallObservations(data) {
    return data?.Content?.find((section) =>
      section?.EnglishCode.startsWith("Overall Observations")
    );
  }

  function transformUnderstandingData(originalData, scoreData) {
    if (!originalData || !scoreData || typeof scoreData.totalScore !== "number")
      return {};

    let transformedData = JSON.parse(JSON.stringify(originalData));
    let totalScore = scoreData.totalScore;

    Object.keys(scoreData.result || {}).forEach((section) => {
      let score = scoreData.result[section]?.score;
      if (typeof score !== "number") return;
      const category = getAdhdScoreCategory(score);
      const totalSectionScore =
        originalData[section]?.Content[category]?.Heading;
      if (!category || !transformedData[section]) return;

      if (transformedData[section]) {
        transformedData[section].Heading = transformedData[
          section
        ].Heading?.replace("{section_score}", score)
          ?.replace("{total_section_score}", totalSectionScore)
          .split(":")
          .map((part) => part.trim());

        if (
          transformedData[section].Content &&
          transformedData[section].Content[category]
        ) {
          transformedData[section].Content =
            transformedData[section].Content[category];
        }
      }
    });

    return transformedData;
  }

  function updateOverallObservations(data, score) {
    if (!data?.overallobservations || typeof score !== "number")
      return { error: "Invalid input" };

    const category = getAdhdScoreCategory(score);

    if (!category) return { error: "Score out of range" };
    if (
      !data.overallobservations.Heading ||
      !data.overallobservations.Content?.[category]
    ) {
      return { error: "Invalid overall observations data" };
    }

    const totalSectionScore =
      data.overallobservations.Content?.[category]?.Heading;

    data.overallobservations.Heading =
      data.overallobservations.Heading?.replace(
        "{section_score}",
        score.toFixed(2)
      )?.replace("{total_section_score}", totalSectionScore);

    data.overallobservations.Content =
      data.overallobservations.Content[category];

    return data.overallobservations;
  }

  const understandingContent = contentData?.understanding?.Content;

  const updatedUsertTraits = transformUserTraitsData(userTraits);

  const transformedData = transformAssessmentData(understandingContent);

  const overallObservationDataComplete = getOverallObservations(
    contentData?.understanding
  );

  const updatedUnderstandingData = transformUnderstandingData(
    transformedData,
    updatedUsertTraits
  );

  const overallObservationData = updateOverallObservations(
    updatedUnderstandingData,
    updatedUsertTraits?.totalScore / 6
  );

  const ProgressBar = ({ label, percentage, color }) => {
    return (
      <li>
        <div className="flex justify-between items-center mb-2">
          <span className="font-semibold text-gray-700">{label}</span>
          <span className="text-sm" style={{ color }}>
            {percentage}%
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-5">
          <div
            className="h-5 rounded-full transition-all duration-500"
            style={{ width: `${percentage}%`, backgroundColor: color }}
          ></div>
        </div>
      </li>
    );
  };

  const totalscoreBySectionContentList =
    contentData?.understanding?.Content.filter((item) => {
      return item.EnglishCode !== "Overall Observations";
    });

  const updatedScoreList = totalscoreBySectionContentList?.map(
    (item, index) => {
      const label = item.Heading.split(":")[0]; // Extracting the label before the colon
      const key = item.EnglishCode?.toLowerCase().replace(/\s+/g, ""); // Creating the key to match updatedUsertTraits
      return {
        label,
        percentage: updatedUsertTraits?.result[key]?.percentage, // Getting percentage from object
        score: updatedUsertTraits?.result[key]?.score, // Getting score from object
        color: progressBarColor[index % progressBarColor.length], // Assigning color cyclically
      };
    }
  );

  const ObservationsPartsHeading = overallObservationData?.Heading
    ? overallObservationData.Heading.split(":").map((part) => part.trim())
    : ["", ""];

  const recommendedData = contentData?.recommendations;

  const resourcesData = contentData?.resources;

  const disclaimerData = contentData?.disclaimer;

  return (
    <>
      <Header />
      <BackToTopButton />
      <div className="min-h-screen">
        <div className="container mx-auto p-4 sm:p-8">
          {isLoading ? (
            <div>
              <Loader isLoading={isLoading} />
            </div>
          ) : (
            <>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  defaultExpanded={true}
                  heroBanner={
                    <HeroBanner title={contentData.header} color="blue" />
                  }
                >
                  {isAuth && (
                    <div className="w-full pt-[10px] flex justify-end lg:justify-end gap-[10px] lg:px-6">
                      <ShareButton
                        currentAssessmentAttemptData={
                          currentAssessmentAttemptData
                        }
                        userGUID={userGUID}
                        userData={userData}
                      />
                      <SendToFriendButton
                        currentAssessmentAttemptData={
                          currentAssessmentAttemptData
                        }
                        userGUID={userGUID}
                        userData={userData}
                        CreateGuestUserConnect={
                          sendToFriendReportOnEmailConnect
                        }
                      />
                    </div>
                  )}
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 mb-6">
                      {/* Left Section (Introduction + Progress Bars) */}
                      <div>
                        <h2 className="text-xl font-semibold text-gray-700 mb-6">
                          {t("report.adhd.introduction")}
                        </h2>
                        <ul className="space-y-4">
                          {updatedScoreList?.map((item, index) => (
                            <ProgressBar
                              key={index}
                              label={item?.label}
                              percentage={item?.percentage}
                              color={item?.color}
                            />
                          ))}
                        </ul>
                      </div>

                      {/* Right Section (Banner Image) */}
                      <div className="flex items-center justify-center">
                        <img
                          src={bannerImage1}
                          alt="Banner Image 1"
                          className="w-full max-h-[400px] object-contain"
                        />
                      </div>
                    </div>

                    <h2 className="text-lg font-semibold text-gray-700 mb-4">
                      {ObservationsPartsHeading[0]}
                    </h2>
                    <p className={`text-lg text-gray-700`}>
                      {ObservationsPartsHeading[1]}
                    </p>
                    <p className={`text-lg text-gray-700`}>
                      {overallObservationDataComplete?.FlavorText}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-8">
                      {overallObservationDataComplete?.Content?.map(
                        (item, index) => {
                          return (
                            <DescriptiveCard
                              heading={item?.Heading}
                              overview={item?.Content}
                              bgClr={
                                descriptiveCardColorMap[
                                  (index + 1) % descriptiveCardColorMap.length
                                ].bg
                              }
                              textClr={
                                descriptiveCardColorMap[
                                  (index + 1) % descriptiveCardColorMap.length
                                ].text
                              }
                            />
                          );
                        }
                      )}
                    </div>
                    <p className={`text-lg text-gray-700`}>
                      {overallObservationDataComplete?.ContentFollowUp}
                    </p>
                  </div>
                </ExpandableSection>
              </section>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={contentData?.understanding?.RootHeading}
                      color="purple"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
                      {[
                        {
                          label: "attentionandfocus",
                          bg: "bg-red-50",
                          text: "text-red-800",
                        },
                        {
                          label: "emotionalregulation",
                          bg: "bg-sky-100",
                          text: "text-sky-800",
                        },
                        {
                          label: "impulsivity",
                          bg: "bg-emerald-50",
                          text: "text-emerald-800",
                        },
                        {
                          label: "timemanagementandprocrastination",
                          bg: "bg-violet-50",
                          text: "text-violet-800",
                        },
                        {
                          label: "hyperactivity",
                          bg: "bg-rose-100",
                          text: "text-rose-800",
                        },
                        {
                          label: "socialinteractions",
                          bg: "bg-blue-50",
                          text: "text-blue-800",
                        },
                      ].map((key, index) => {
                        return (
                          <div key={key.label} className="flex flex-col">
                            {/* Section Title */}
                            <h2 className="text-lg font-semibold text-gray-700 mb-2">
                              {
                                updatedUnderstandingData?.[key.label]
                                  ?.Heading?.[0]
                              }{" "}
                            </h2>
                            <p className="text-lg text-gray-700 mb-2">
                              {
                                updatedUnderstandingData?.[key.label]
                                  ?.Heading?.[1]
                              }{" "}
                            </p>

                            <DescriptiveCard
                              heading={
                                updatedUnderstandingData?.[key.label]?.Content
                                  ?.Heading
                              }
                              overview={
                                updatedUnderstandingData?.[key.label]?.Content
                                  ?.Content
                              }
                              bgClr={key.bg}
                              textClr={key.text}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </ExpandableSection>
              </section>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={recommendedData?.RootHeading}
                      color="red"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <p className={`text-lg text-gray-700`}>
                      {recommendedData?.FlavorText}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 my-6 mb-10">
                      <div>
                        {recommendedData?.Content && (
                          <ul className="space-y-3 mt-auto">
                            {recommendedData?.Content?.map((item, index) => (
                              <li
                                key={index}
                                className="flex gap-4 items-start"
                              >
                                {/* SVG Icon */}
                                <div className="w-6 h-6 flex-shrink-0">
                                  <svg
                                    width="100%"
                                    height="100%"
                                    viewBox="0 0 48 48"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <defs>
                                      <filter
                                        id="shadow"
                                        x="-20%"
                                        y="-20%"
                                        width="140%"
                                        height="140%"
                                      >
                                        <feDropShadow
                                          dx="0"
                                          dy="2"
                                          stdDeviation="3"
                                          floodColor="black"
                                          floodOpacity="0.4"
                                        />
                                      </filter>
                                    </defs>
                                    <path
                                      d="M24 33C28.9706 33 33 28.9706 33 24C33 19.0294 28.9706 15 24 15C19.0294 15 15 19.0294 15 24C15 28.9706 19.0294 33 24 33Z"
                                      fill="#346bcc"
                                      stroke="#fff"
                                      strokeWidth="6"
                                      filter="url(#shadow)"
                                    />
                                  </svg>
                                </div>

                                {/* Text Content */}
                                <div className="flex flex-col">
                                  <strong className="text-[#346bcc] leading-tight">
                                    {item?.Heading}
                                  </strong>
                                  <p className="">{item?.Content}</p>
                                </div>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <img
                        src={bannerImage2}
                        alt="Banner Image 1"
                        className="w-full h-full object-contain"
                      />
                    </div>
                  </div>
                </ExpandableSection>
              </section>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={resourcesData?.RootHeading}
                      color="green"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <p className={`text-lg text-gray-700`}>
                      {resourcesData?.FlavorText}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 my-6 mb-10">
                      {resourcesData?.Content?.map((item, index) => (
                        <InfoListCard
                          key={index}
                          bgClr={colors[index % colors.length]}
                          heading={item?.Heading}
                          contentArray={item?.Content}
                        />
                      ))}
                    </div>

                    <DescriptiveCard
                      heading={disclaimerData?.RootHeading}
                      overview={disclaimerData?.Content}
                      bgClr={"bg-green-50"}
                      textClr={"text-green-800"}
                    />
                  </div>
                </ExpandableSection>
              </section>
              {isAuth ? (
                <CallToActionSection
                  currentLocale={currentLocale}
                  t={t}
                  getAssessmentConnect={getAssessmentConnect}
                  getAssessmentAttemptByUserIdConnect={
                    getAssessmentAttemptByUserIdConnect
                  }
                  assessmentId={assessmentId}
                  userId={userGUID}
                  bgColor={ASSESSMENT_BACKGROUND_COLORS.BLUE_INDIGO}
                />
              ) : (
                <CallToActionSectionForUnAuth
                  onClick1={() => {
                    navigate(
                      `${quizPath.next}?assessmentId=${PERSONALITY_TEST_ASSESSMENT_ID}&newt=true`
                    );
                  }}
                  onClick2={() => {
                    navigate(
                      `${quizPath.next}?assessmentId=${BIG_FIVE_TEST_ASSESSMENT_ID}&newt=true`
                    );
                  }}
                />
              )}

              {isAuth && (
                <InviteFriend
                  sectionMode={true}
                  getGuestUserConnect={getGuestUserConnect}
                  getOrderDataByUserIdConnect={getOrderDataByUserIdConnect}
                  getBillingConnect={getBillingConnect}
                  guestUserChangesConnect={guestUserChangesConnect}
                  isGuestOrPremiumUserConnect={isGuestOrPremiumUserConnect}
                  userId={userGUID}
                  userEmail={userData?.email}
                  buttonText={t("dashboard.invite_friend_cta")}
                  selectedPlan={selectedPlan}
                  assessmentData={currentAssessmentAttemptData}
                  showRemainingInvites={true}
                  bgColor={ASSESSMENT_BACKGROUND_COLORS.PURPLE_PINK}
                />
              )}
            </>
          )}
          <ReportWrapper
            assessmentId={assessmentId}
            assessmentAttemptId={assessmentAttemptId}
            currentLocale={currentLocale}
          />
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ADHDReport;
